/**
 * VoiceLogRecorder — a mobile-friendly custom audio recorder.
 *
 * Flow:
 *   IDLE       → user taps Record → permission prompt → RECORDING
 *   RECORDING  → user taps Stop  → REVIEW
 *   REVIEW     → listen back / restart / discard / submit → DONE
 *
 * Browser support:
 *   - iOS Safari 14.3+   (records to audio/mp4 aac)
 *   - Android Chrome     (audio/webm; codecs=opus)
 *   - Desktop Chrome/FF  (audio/webm; codecs=opus or audio/ogg)
 *
 * We probe MediaRecorder.isTypeSupported at start-up and pick whichever the
 * browser advertises so the same component works everywhere without polyfills.
 *
 * The recording is capped at 60 seconds client-side + 75 seconds server-side.
 * We stream the visible timer with requestAnimationFrame for smoothness.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Play, Pause, Trash2, RotateCcw, Send, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const MAX_SECONDS = 60;

// Ordered preference — first match wins. mp4 must appear before webm because
// iOS Safari lies about webm support in some builds; when both report true we
// still want the codec that actually plays back inline on iOS.
const PREFERRED_MIMES = [
    "audio/mp4",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
];

function pickSupportedMime() {
    if (typeof MediaRecorder === "undefined") return null;
    for (const m of PREFERRED_MIMES) {
        try {
            if (MediaRecorder.isTypeSupported(m)) return m;
        } catch { /* ignore probe errors on old browsers */ }
    }
    return ""; // let the browser choose its default
}

function fmt(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60).toString().padStart(1, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
}

async function blobToBase64(blob) {
    const buf = await blob.arrayBuffer();
    let s = "";
    const bytes = new Uint8Array(buf);
    // Chunked to avoid stack overflow on large-ish arrays.
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        s += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(s);
}

// Encode a Float32Array PCM channel as a mono 16-bit PCM WAV blob.
// Used only when the user trims — keeps the pipeline pure browser APIs.
function pcmToWavBlob(pcm, sampleRate) {
    const numSamples = pcm.length;
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);
    const writeString = (offset, str) => {
        for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeString(0, "RIFF");
    view.setUint32(4, 36 + numSamples * 2, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);         // PCM header size
    view.setUint16(20, 1, true);          // PCM format
    view.setUint16(22, 1, true);          // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, "data");
    view.setUint32(40, numSamples * 2, true);
    let off = 44;
    for (let i = 0; i < numSamples; i++) {
        const s = Math.max(-1, Math.min(1, pcm[i]));
        view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        off += 2;
    }
    return new Blob([buffer], { type: "audio/wav" });
}

// Decode the recorded blob, slice to [startSec, endSec], downmix to mono,
// and resample to `outRate` Hz. Returns { blob, mime, duration }.
async function trimBlob(blob, startSec, endSec, outRate = 22050) {
    const arrayBuf = await blob.arrayBuffer();
    const AudioCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    // First decode with a regular AudioContext so we can read the natural
    // sample rate — some browsers refuse to decode into an OfflineAudioContext
    // with a mismatched rate.
    const decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
    let decoded;
    try {
        decoded = await decodeCtx.decodeAudioData(arrayBuf.slice(0));
    } finally {
        try { await decodeCtx.close(); } catch { /* noop */ }
    }
    const clampedStart = Math.max(0, Math.min(startSec, decoded.duration));
    const clampedEnd = Math.max(clampedStart, Math.min(endSec, decoded.duration));
    const duration = clampedEnd - clampedStart;
    if (duration <= 0) throw new Error("Trim range is empty.");
    const outLength = Math.max(1, Math.round(duration * outRate));
    const offline = new AudioCtx(1, outLength, outRate);
    const src = offline.createBufferSource();
    src.buffer = decoded;
    src.connect(offline.destination);
    src.start(0, clampedStart, duration);
    const rendered = await offline.startRendering();
    const pcm = rendered.getChannelData(0);
    return { blob: pcmToWavBlob(pcm, outRate), mime: "audio/wav", duration };
}

export default function VoiceLogRecorder({
    open,
    onClose,
    defaultLocation = "",
    defaultRegion = "",
    onSubmitted,
}) {
    const [phase, setPhase] = useState("idle"); // idle | recording | review | submitting | done
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState("");
    const [blob, setBlob] = useState(null);
    const [blobUrl, setBlobUrl] = useState("");
    const [playing, setPlaying] = useState(false);
    // Trim window in seconds — set to full recording on stop, adjusted via
    // the dual-range slider before submit.
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(0);
    const [form, setForm] = useState({
        name: "",
        location: defaultLocation,
        region: defaultRegion,
        message: "",
    });

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioRef = useRef(null);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const mimeRef = useRef("");

    // Reset when the modal reopens so a fresh session always feels clean.
    useEffect(() => {
        if (open) {
            setPhase("idle");
            setElapsed(0);
            setError("");
            setBlob(null);
            setBlobUrl((u) => { if (u) URL.revokeObjectURL(u); return ""; });
            setPlaying(false);
            setForm((f) => ({
                name: f.name,
                location: defaultLocation || f.location,
                region: defaultRegion || f.region,
                message: "",
            }));
        }
        return () => stopEverything();
    }, [open]);

    function stopEverything() {
        try { mediaRecorderRef.current?.stop(); } catch { /* already stopped */ }
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        if (timerRef.current) cancelAnimationFrame(timerRef.current);
        timerRef.current = null;
    }

    function tickTimer() {
        const now = performance.now();
        const s = (now - startedAtRef.current) / 1000;
        setElapsed(s);
        if (s >= MAX_SECONDS) {
            // Hard cap so mobile users can't accidentally record for 20 minutes.
            handleStop();
            return;
        }
        timerRef.current = requestAnimationFrame(tickTimer);
    }

    async function handleRecord() {
        setError("");
        if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
            setError("Your browser doesn't support microphone recording. Try Safari or Chrome on a modern device.");
            return;
        }
        if (typeof MediaRecorder === "undefined") {
            setError("Recording isn't supported here. Please try a newer browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
            const mime = pickSupportedMime();
            mimeRef.current = mime || "audio/webm";
            const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (ev) => {
                if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
            };
            rec.onstop = () => {
                const finalMime = mimeRef.current || rec.mimeType || "audio/webm";
                const b = new Blob(chunksRef.current, { type: finalMime });
                setBlob(b);
                const url = URL.createObjectURL(b);
                setBlobUrl(url);
                // Initialise the trim window to the full recording — user can
                // then drag the handles inward before submitting.
                const finalElapsed = (performance.now() - startedAtRef.current) / 1000;
                const cappedDur = Math.min(finalElapsed, MAX_SECONDS);
                setTrimStart(0);
                setTrimEnd(cappedDur);
                setPhase("review");
                // Release the mic so the browser stops showing the recording chip.
                streamRef.current?.getTracks().forEach((t) => t.stop());
            };
            rec.start(250); // deliver chunks every 250 ms for accurate durations
            mediaRecorderRef.current = rec;
            startedAtRef.current = performance.now();
            setElapsed(0);
            setPhase("recording");
            timerRef.current = requestAnimationFrame(tickTimer);
        } catch (err) {
            const msg = err?.name === "NotAllowedError"
                ? "Microphone access was denied. Please allow it in your browser settings and try again."
                : (err?.message || "Couldn't start the microphone.");
            setError(msg);
        }
    }

    function handleStop() {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
        }
        if (timerRef.current) cancelAnimationFrame(timerRef.current);
        timerRef.current = null;
    }

    function handleRestart() {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlob(null);
        setBlobUrl("");
        setPlaying(false);
        setElapsed(0);
        setPhase("idle");
    }

    function handleDiscard() {
        handleRestart();
    }

    function togglePlayback() {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) {
            el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
            el.pause();
            setPlaying(false);
        }
    }

    async function handleSubmit(e) {
        e?.preventDefault?.();
        setError("");
        if (!blob) { setError("Record a message first."); return; }
        if (!form.name.trim()) { setError("Add your name so I know who I'm hearing from."); return; }
        if (!form.location.trim()) { setError("Add the location this message is about."); return; }
        setPhase("submitting");
        try {
            // If the user tightened the trim window, decode + slice + WAV-encode
            // the trimmed portion. Otherwise submit the original blob as-is so
            // we keep the small opus/aac payload from the browser.
            const fullDuration = Math.min(elapsed, MAX_SECONDS);
            const trimmed = trimStart > 0.05 || (fullDuration - trimEnd) > 0.05;
            let finalBlob = blob;
            let finalMime = blob.type || mimeRef.current || "audio/webm";
            let finalDuration = fullDuration;
            if (trimmed) {
                const res = await trimBlob(blob, trimStart, trimEnd);
                finalBlob = res.blob;
                finalMime = res.mime;
                finalDuration = res.duration;
            }
            const audio_base64 = await blobToBase64(finalBlob);
            await api.post("/voice_logs/submit", {
                name: form.name.trim(),
                location: form.location.trim(),
                region: form.region.trim(),
                message: form.message.trim(),
                lat: null,
                lng: null,
                duration_seconds: finalDuration,
                audio_mime: finalMime,
                audio_base64,
            });
            setPhase("done");
            toast.success("Thanks! Your voice log has been sent for review.");
            if (onSubmitted) onSubmitted({ status: "pending" });
            // Small delay before auto-closing so the "done" state is seen.
            setTimeout(() => { onClose?.(); }, 1400);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Couldn't send your recording. Try again in a moment.";
            setError(String(msg));
            setPhase("review");
        }
    }

    if (!open) return null;

    return (
        <AnimatePresence>
            <motion.div
                key="voicelog-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
                data-testid="voice-log-modal"
                onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            >
                <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 40, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 28 }}
                    className="w-full sm:max-w-lg bg-[var(--sb-bg)] border-t sm:border border-[var(--sb-border)] sm:rounded-lg p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    <header className="flex items-start justify-between mb-6">
                        <div>
                            <div className="overline mb-2" style={{ color: "#0055A4" }}>
                                <span className="editorial-rule" style={{ background: "#0055A4" }} />
                                Visitor Voice Log
                            </div>
                            <h3 className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight">
                                    Tell me about<br/>
                                    <span className="italic" style={{ color: "#0055A4" }}>where you&apos;ve been.</span>
                            </h3>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] transition-colors"
                            data-testid="voice-log-close"
                            aria-label="Close voice log recorder"
                        >
                            <X size={22} />
                        </button>
                    </header>

                    {/* Recording panel */}
                    <div className="border border-[var(--sb-border)] p-5 mb-6" data-testid="voice-log-panel">
                        {phase === "idle" && (
                            <div className="flex flex-col items-center gap-4 py-6">
                                <button
                                    type="button"
                                    onClick={handleRecord}
                                    className="w-20 h-20 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
                                    style={{ background: "#0055A4", boxShadow: "0 0 40px -8px rgba(0,85,164,0.7)" }}
                                    data-testid="voice-log-record-start"
                                    aria-label="Start recording"
                                >
                                    <Mic size={32} />
                                </button>
                                <p className="text-sm text-[var(--sb-text-dim)] text-center">
                                    Tap the microphone and speak. Max 60 seconds.
                                </p>
                            </div>
                        )}

                        {phase === "recording" && (
                            <div className="flex flex-col items-center gap-4 py-6">
                                <button
                                    type="button"
                                    onClick={handleStop}
                                    className="w-20 h-20 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 animate-pulse"
                                    style={{ background: "#c0392b", boxShadow: "0 0 50px -6px rgba(192,57,43,0.75)" }}
                                    data-testid="voice-log-record-stop"
                                    aria-label="Stop recording"
                                >
                                    <Square size={28} fill="white" />
                                </button>
                                <div className="text-center">
                                    <div
                                        className="font-mono text-3xl tabular-nums text-[var(--sb-text)]"
                                        data-testid="voice-log-timer"
                                    >
                                        {fmt(elapsed)}
                                    </div>
                                    <div className="w-full max-w-[240px] h-1 bg-[var(--sb-border)] mt-3 relative overflow-hidden rounded-full">
                                        <div
                                            className="h-full transition-[width] duration-100"
                                            style={{ width: `${Math.min(100, (elapsed / MAX_SECONDS) * 100)}%`, background: "#0055A4" }}
                                        />
                                    </div>
                                    <p className="text-xs text-[var(--sb-text-muted)] mt-2 uppercase tracking-[0.28em]">Recording · Tap to stop</p>
                                </div>
                            </div>
                        )}

                        {phase === "review" && (
                            <div className="flex flex-col items-center gap-4">
                                <audio
                                    ref={audioRef}
                                    src={blobUrl}
                                    onEnded={() => setPlaying(false)}
                                    onPause={() => setPlaying(false)}
                                    onPlay={() => setPlaying(true)}
                                    className="w-full mt-1"
                                    controls
                                    data-testid="voice-log-playback"
                                />
                                <div className="text-xs text-[var(--sb-text-muted)] uppercase tracking-[0.28em]">
                                    {fmt(elapsed)} · {(blob?.size / 1024).toFixed(0)} KB
                                </div>

                                {/* Trim window — dual range slider. Only meaningful when
                                    the recording is at least a second long. */}
                                {elapsed >= 1 && (
                                    <div className="w-full mt-2" data-testid="voice-log-trim">
                                        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[var(--sb-text-muted)] mb-2">
                                            <span>Trim</span>
                                            <span data-testid="voice-log-trim-readout">
                                                {fmt(trimStart)} → {fmt(trimEnd)} · {(trimEnd - trimStart).toFixed(1)}s
                                            </span>
                                        </div>
                                        <div className="relative h-8 select-none">
                                            {/* Track */}
                                            <div className="absolute inset-y-3 left-0 right-0 rounded-full bg-[var(--sb-border)]" />
                                            {/* Selected range highlight */}
                                            <div
                                                className="absolute inset-y-3 rounded-full"
                                                style={{
                                                    left: `${(trimStart / Math.max(0.001, elapsed)) * 100}%`,
                                                    right: `${100 - (trimEnd / Math.max(0.001, elapsed)) * 100}%`,
                                                    background: "#0055A4",
                                                }}
                                            />
                                            <input
                                                type="range"
                                                min={0}
                                                max={elapsed}
                                                step={0.1}
                                                value={trimStart}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value);
                                                    setTrimStart(Math.min(v, trimEnd - 0.2));
                                                }}
                                                className="voice-log-trim-thumb absolute inset-0 w-full appearance-none bg-transparent pointer-events-auto"
                                                data-testid="voice-log-trim-start"
                                                aria-label="Trim start"
                                            />
                                            <input
                                                type="range"
                                                min={0}
                                                max={elapsed}
                                                step={0.1}
                                                value={trimEnd}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value);
                                                    setTrimEnd(Math.max(v, trimStart + 0.2));
                                                }}
                                                className="voice-log-trim-thumb absolute inset-0 w-full appearance-none bg-transparent pointer-events-auto"
                                                data-testid="voice-log-trim-end"
                                                aria-label="Trim end"
                                            />
                                        </div>
                                        {(trimStart > 0.05 || (elapsed - trimEnd) > 0.05) && (
                                            <div className="flex items-center justify-between mt-2">
                                                <p className="text-[11px] text-[var(--sb-text-muted)]">
                                                    Trimmed clip will be re-encoded to WAV.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => { setTrimStart(0); setTrimEnd(elapsed); }}
                                                    className="text-[11px] uppercase tracking-[0.2em] text-[var(--sb-brand)] hover:text-white transition-colors"
                                                    data-testid="voice-log-trim-reset"
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2 justify-center mt-1">
                                    <button
                                        type="button"
                                        onClick={togglePlayback}
                                        className="btn-outline text-sm"
                                        data-testid="voice-log-toggle-playback"
                                        style={{ padding: "0.55rem 1rem" }}
                                    >
                                        {playing ? <><Pause size={14}/> Pause</> : <><Play size={14}/> Play</>}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleRestart}
                                        className="btn-outline text-sm"
                                        data-testid="voice-log-restart"
                                        style={{ padding: "0.55rem 1rem" }}
                                    >
                                        <RotateCcw size={14}/> Restart
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDiscard}
                                        className="btn-outline text-sm"
                                        data-testid="voice-log-discard"
                                        style={{ padding: "0.55rem 1rem" }}
                                    >
                                        <Trash2 size={14}/> Discard
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Metadata form — always visible but only submitted after a
                        recording exists. Kept short so mobile keyboards don't
                        overwhelm the layout. */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="overline block mb-1">Your name</label>
                            <input
                                type="text"
                                className="input-line"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                maxLength={80}
                                required
                                data-testid="voice-log-name-input"
                                placeholder="Alex"
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="overline block mb-1">Location</label>
                                <input
                                    type="text"
                                    className="input-line"
                                    value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                    maxLength={120}
                                    required
                                    data-testid="voice-log-location-input"
                                    placeholder="Old Man of Storr"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-1">Region</label>
                                <select
                                    className="input-line"
                                    value={form.region}
                                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                                    data-testid="voice-log-region-input"
                                >
                                    <option value="">Choose…</option>
                                    <option value="Highlands">Highlands</option>
                                    <option value="Islands">Islands</option>
                                    <option value="Cities">Cities</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-1">Short note (optional)</label>
                            <textarea
                                className="input-line"
                                rows={2}
                                maxLength={500}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                data-testid="voice-log-message-input"
                                placeholder="Why this place stayed with you…"
                            />
                        </div>

                        {error && (
                            <div className="text-sm text-[#ff9d9d] border border-[#c0392b]/50 bg-[#c0392b]/10 px-3 py-2" data-testid="voice-log-error">
                                {error}
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-3 pt-2">
                            <p className="text-xs text-[var(--sb-text-muted)]">
                                Recordings are reviewed before being published.
                            </p>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={phase !== "review" || !blob}
                                data-testid="voice-log-submit"
                            >
                                {phase === "submitting" ? <><Loader2 size={16} className="animate-spin"/> Sending…</>
                                    : phase === "done" ? <>Sent · Thank you</>
                                    : <><Send size={16}/> Send voice log</>}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
